import { z } from "zod";

import { zPolymorphicId } from "../zod-format";
import { reviewQuestionSchema } from "./review-question";

const reviewEnrichedAnswerSchema = z.object({
  questionId: z.string().min(1),
  selectedOptionIds: z.array(z.string()),
  customAnswer: z.string().optional(),
  selectedOptions: z.array(
    z.object({
      id: z.string().min(1),
      label: z.string().min(1),
      payload: z.record(z.string(), z.unknown()).optional(),
    }),
  ),
});

const attachedResourceSchema = z.object({
  kind: z.enum(["upload", "record", "submission", "account"]),
  // `kind` names a review role, not a registry type, so any registered id may fill it.
  publicId: zPolymorphicId("any"),
});

export const humanReviewInputSchema = z.object({
  context: z.record(z.string(), z.unknown()).default({}),
  attachedResources: z.array(attachedResourceSchema).optional(),
});

export const humanReviewConfigSchema = z
  .object({
    title: z.string().min(1),
    description: z.string().nullish(),
    /** Inline question authoring. Mutually exclusive with `questionsFrom` —
     *  when both are set, `questionsFrom` wins at runtime, so the editor
     *  should hide one section when the other is in use.
     *
     *  `nullish` (allows undefined or null) so the editor can "clear" the
     *  field by patching `null` without violating the schema. */
    questions: z.array(reviewQuestionSchema).nullish(),
    /** Selector that resolves to a `ReviewQuestion[]` from upstream node
     *  output (a `{{ steps.<node>.output.* }}` template tag).
     *  Use when the questions are dynamic — e.g. derived from agent
     *  uncertainty. */
    questionsFrom: z.string().nullish(),
    assignee: z
      .discriminatedUnion("kind", [
        z.object({ kind: z.literal("user"), publicId: z.string().min(1) }),
        z.object({ kind: z.literal("team"), publicId: z.string().min(1) }),
        z.object({ kind: z.literal("agent"), publicId: z.string().min(1) }),
      ])
      .nullish(),
    skipIfConfidenceAtLeast: z
      .object({
        selector: z.string().min(1),
        threshold: z.number().min(0).max(1),
      })
      .nullish(),
  })
  .refine(
    (cfg) => {
      const hasInline = (cfg.questions?.length ?? 0) > 0;
      const hasSelector =
        typeof cfg.questionsFrom === "string" && cfg.questionsFrom.trim().length > 0;
      return hasInline || hasSelector;
    },
    {
      message: "Either `questions` (non-empty) or `questionsFrom` must be set",
      path: ["questions"],
    },
  );

export const humanReviewOutputSchema = z.object({
  status: z.enum(["resolved", "skipped"]),
  /** Null when status="skipped" (no review record was created). */
  reviewPublicId: z.string().min(1).nullable(),
  resolvedAt: z.string().nullable(),
  resolvedByUserPublicId: z.string().nullable(),
  answers: z.array(reviewEnrichedAnswerSchema),
});

export type HumanReviewInput = z.infer<typeof humanReviewInputSchema>;
export type HumanReviewConfig = z.infer<typeof humanReviewConfigSchema>;
export type HumanReviewOutput = z.infer<typeof humanReviewOutputSchema>;
